import { useEffect, useMemo, useState } from "react";
import "@/App.css";

import { api } from "@/lib/api";
import { Landing } from "@/components/Landing";
import { StartPage } from "@/components/StartPage";
import { Phase2Conversation } from "@/components/Phase2Conversation";
import { Phase3Output } from "@/components/Phase3Output";
import { ConfigPanel } from "@/components/ConfigPanel";
import { DemoPlayer } from "@/components/DemoPlayer";
import { useVoiceTranscription } from "@/hooks/useVoiceTranscription";

const STORAGE_KEY = "imagine_ai_session_id";

function App() {
  const [session, setSession] = useState(null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet");
  const [phase, setPhase] = useState("context"); // context | conversation
  const [started, setStarted] = useState(false); // false = on marketing landing; true = in workspace
  const [demoOpen, setDemoOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalyzeCta, setShowAnalyzeCta] = useState(false);
  const [error, setError] = useState("");
  const [followupValue, setFollowupValue] = useState("");
  const [followupBusy, setFollowupBusy] = useState(false);
  const [retryDraft, setRetryDraft] = useState("");
  const [configOpen, setConfigOpen] = useState(false);

  // Voice hooks: one for the chat composer, one for the followup input.
  const chatVoice = useVoiceTranscription("");
  const followupVoice = useVoiceTranscription("");

  // When followup voice finalises while user is on the output screen, push it into the followup input.
  useEffect(() => {
    if (followupVoice.transcript && !followupVoice.listening) {
      setFollowupValue((prev) => (prev ? `${prev} ${followupVoice.transcript}`.trim() : followupVoice.transcript));
      followupVoice.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupVoice.listening]);

  // Bootstrap: load models, then load or create session.
  useEffect(() => {
    (async () => {
      try {
        const m = await api.listModels();
        setModels(m.models);
        setModel(m.default);

        const stored = localStorage.getItem(STORAGE_KEY);
        let s = null;
        if (stored) {
          try {
            s = await api.getSession(stored);
          } catch (_) {
            s = null;
          }
        }
        if (!s) {
          s = await api.createSession();
          localStorage.setItem(STORAGE_KEY, s.id);
        }
        setSession(s);
        if (s?.current_model) setModel(s.current_model);
        if (["conversation", "output", "followup"].includes(s?.phase)) {
          setPhase("conversation");
          setStarted(true);
          setShowAnalyzeCta(s.ready_for_analysis && !s.analyses?.[s.current_model]);
        }
      } catch (e) {
        setError("Could not reach the server. Please try again.");
      }
    })();
  }, []);

  const currentAnalysis = useMemo(() => {
    if (!session) return null;
    return session.analyses?.[model] || null;
  }, [session, model]);

  const handleBegin = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.begin(session.id, model);
      setSession(res.session);
      setPhase("conversation");
      setShowAnalyzeCta(res.action === "ready");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong starting the conversation.");
    } finally {
      setBusy(false);
    }
  };

  const doSend = async (message) => {
    if (!session) return;
    setSession((prev) =>
      prev
        ? {
            ...prev,
            conversation: [...(prev.conversation || []), { role: "user", content: message, ts: new Date().toISOString() }],
          }
        : prev,
    );
    setThinking(true);
    setBusy(true);
    setError("");
    setRetryDraft("");
    try {
      const res = await api.chat(session.id, message, model);
      setSession(res.session);
      setShowAnalyzeCta(res.action === "ready");
    } catch (e) {
      // Rollback optimistic message and offer retry.
      setSession((prev) =>
        prev ? { ...prev, conversation: (prev.conversation || []).slice(0, -1) } : prev,
      );
      setRetryDraft(message);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 502) {
        setError(detail || "The model returned an unexpected response. I've put your message back — try again.");
      } else if (!status) {
        setError("Couldn't reach the server (it may be restarting). I've put your message back — try again.");
      } else {
        setError(detail || "Something went wrong. I've put your message back — try again.");
      }
    } finally {
      setThinking(false);
      setBusy(false);
    }
  };

  const openConfigOrAnalyze = () => {
    // If we already have an analysis for this model, just re-open the config panel.
    setConfigOpen(true);
  };

  const handleAnalyze = async (inputs) => {
    if (!session) return;
    setAnalyzing(true);
    setBusy(true);
    setError("");
    try {
      // Ensure the loading copy gets at least one full cycle even on fast responses.
      const [res] = await Promise.all([
        api.analyze(session.id, model, inputs || {}),
        new Promise((r) => setTimeout(r, 2400)),
      ]);
      setSession(res.session);
      setShowAnalyzeCta(false);
      setConfigOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not produce the analysis. Try again.");
    } finally {
      setAnalyzing(false);
      setBusy(false);
    }
  };

  const handleNewFeature = async () => {
    if (!session) return;
    if (!window.confirm("Start a new feature? Your product context is kept; the feature idea and conversation reset."))
      return;
    const updated = await api.newFeature(session.id);
    setSession(updated);
    setPhase("context");
    setShowAnalyzeCta(false);
    setFollowupValue("");
    setError("");
  };

  const handleFollowup = async () => {
    if (!session || !followupValue.trim()) return;
    setFollowupBusy(true);
    setError("");
    const message = followupValue.trim();
    setFollowupValue("");
    try {
      const res = await api.followup(session.id, message, model);
      setSession(res.session);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send your follow-up.");
    } finally {
      setFollowupBusy(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#56534A] text-sm" data-testid="loading">
        Loading…
      </div>
    );
  }

  if (phase === "context") {
    if (!started) {
      return (
        <>
          <Landing
            onBeginScroll={() => setStarted(true)}
            onWatchDemo={() => setDemoOpen(true)}
          />
          {demoOpen && <DemoPlayer onExit={() => setDemoOpen(false)} />}
        </>
      );
    }
    return (
      <>
        <StartPage
          session={session}
          setSession={setSession}
          onBegin={handleBegin}
          onExit={() => setStarted(false)}
          busy={busy}
          error={error}
        />
        {demoOpen && <DemoPlayer onExit={() => setDemoOpen(false)} />}
      </>
    );
  }

  return (
    <Phase2Conversation
      session={session}
      models={models}
      model={model}
      setModel={setModel}
      onSendChip={doSend}
      onSendText={doSend}
      onAnalyze={openConfigOrAnalyze}
      onNewFeature={handleNewFeature}
      busy={busy}
      thinking={thinking}
      analyzing={analyzing}
      showAnalyzeCta={showAnalyzeCta && !currentAnalysis}
      retryDraft={retryDraft}
      clearRetryDraft={() => setRetryDraft("")}
      voice={chatVoice}
    >
      {configOpen && !currentAnalysis && (
        <ConfigPanel
          onRun={(inputs) => handleAnalyze(inputs)}
          onCancel={() => setConfigOpen(false)}
          busy={analyzing}
        />
      )}
      {currentAnalysis && (
        <Phase3Output
          analysis={currentAnalysis}
          modelLabel={models.find((m) => m.key === model)?.label || model}
          onReanalyze={() => setConfigOpen(true)}
          busy={analyzing}
          followupValue={followupValue}
          setFollowupValue={setFollowupValue}
          onFollowupSubmit={handleFollowup}
          followupBusy={followupBusy}
          followupListening={followupVoice.listening}
          onFollowupMicToggle={followupVoice.toggle}
          followupSupported={followupVoice.supported}
        />
      )}
      {error && (
        <p className="text-sm text-[#C4521E] mt-4" data-testid="conversation-error">
          {error}
        </p>
      )}
    </Phase2Conversation>
  );
}

export default App;
